import * as THREE from 'three';
import { UDPGameClient } from './utils/UDPGameClient';
import { Tween, Group } from '@tweenjs/tween.js';
import * as TWEEN from '@tweenjs/tween.js';
import { CharacterControllerBase } from './components/Character';
import { createTextSprite } from './utils/TextSprite';
import { ChatUI } from './components/ChatUI';
import { WS_CLIENT_URL, MAX_RECONNECT_ATTEMPTS } from './config/env';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GameScoring } from './utils/GameScoring';

export class MultiplayerManager {
    private client!: UDPGameClient;  // Using definite assignment assertion
    private remotePlayers: Map<string, THREE.Group> = new Map();
    private localPlayer: {
        model: THREE.Group;
        controller: CharacterControllerBase;
    };
    private scene: THREE.Scene;
    private nameLabels: Map<string, THREE.Sprite> = new Map();
    private playerName: string;
    private chatUI: ChatUI | null = null;
    private heartbeatInterval: number | null = null;
    private lastPosition: { x: number, y: number, z: number } | null = null;
    private lastRotation: { y: number } | null = null;
    private gameScoring!: GameScoring;
    private remoteDanceStopFlags: Map<string, boolean> = new Map();

    constructor(serverUrl: string, playerName: string = "Player", scene: THREE.Scene, localPlayer: { model: THREE.Group; controller: CharacterControllerBase }) {
        this.localPlayer = localPlayer;
        this.playerName = playerName;
        this.scene = scene;
        if (!this.scene) {
            console.error('Local player must be added to a scene before initializing MultiplayerManager');
            return;
        }
        
        // Log the WebSocket URL being used
        console.log('Connecting to WebSocket server at:', serverUrl);
        
        console.log('MultiplayerManager constructor called with:', {
            localPlayer,
            scene: this.scene,
            sceneChildren: this.scene?.children.length
        });
        
        // Ensure we have a valid WebSocket URL
        const wsUrl = WS_CLIENT_URL || 'ws://localhost:8080';
        
        // Use environment configuration for WebSocket URL with explicitly defined playerName
        const actualPlayerName = this.playerName || 'Player';
        this.client = new UDPGameClient(wsUrl, actualPlayerName);
        
        // Initialize game scoring
        this.gameScoring = new GameScoring(this.client);
        
        // Set up event handlers
        this.client.on('gameState', (state: any) => {
            this.handleGameState(state);
        });
        this.client.on('player_joined', (data: any) => this.handlePlayerJoined(data));
        this.client.on('player_left', (data: any) => this.handlePlayerLeft(data));

        // Start heartbeat
        this.startHeartbeat();
    }

    private startHeartbeat(): void {
        // Clear any existing heartbeat
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        // Send heartbeat every 5 seconds (half of server timeout)
        this.heartbeatInterval = window.setInterval(() => {
            if (this.lastPosition && this.lastRotation) {
                this.client.updatePosition(
                    this.lastPosition,
                    this.lastRotation,
                    {
                        name: this.playerName || 'Player',
                        isWalking: false // During heartbeat, assume not walking
                    }
                );
            }
        }, 5000);
    }

    private handleGameState(state: any) {
        const localPlayerId = this.client.getPlayerId();

        // Ensure state.players exists before processing
        if (!state.players) {
            console.error('Game state missing players object:', state);
            return;
        }

        // Update or create other players
        for (const [playerId, playerData] of Object.entries<any>(state.players)) {
            if (playerId === localPlayerId) {
                continue; // Skip local player
            }

            if (!this.remotePlayers.has(playerId)) {
                console.log('Creating new remote player in scene:', {
                    id: playerId,
                    position: playerData.position,
                    name: playerData.game_data?.name || 'Unknown Player',
                    currentPlayers: this.remotePlayers.size,
                    playerData // Log the full player data for debugging
                });
                this.createRemotePlayer(playerId, playerData);
            } else {
                // Update existing remote player
                this.updateRemotePlayer(playerId, playerData);
            }
        }

        // Remove players that are no longer in the game state
        for (const playerId of this.remotePlayers.keys()) {
            if (!state.players[playerId]) {
                console.log('Removing player no longer in game state:', playerId);
                this.removeRemotePlayer(playerId);
            }
        }
    }

    private handlePlayerJoined(data: any) {
        console.log('Player joined event:', {
            id: data.player_id,
            position: data.position,
            name: data.game_data?.name
        });
        this.createRemotePlayer(data.player_id, data);
    }

    private handlePlayerLeft(data: any) {
        console.log('Player left event:', {
            id: data.player_id
        });
        this.removeRemotePlayer(data.player_id);
    }

    private createRemotePlayer(playerId: string, playerData: any) {
        if (this.remotePlayers.has(playerId)) {
            console.warn(`Player ${playerId} already exists in remotePlayers`);
            return;
        }
        
        // Create new player instance
        const newPlayer = this.createPlayerInstance();
        
        // Add to remotePlayers map
        this.remotePlayers.set(playerId, newPlayer);
        
        // Add to scene
        this.scene.add(newPlayer);
        
        // Create name label for remote player
        // Use playerData.game_data.name or a default name if undefined
        const playerName = (playerData.game_data?.name || `Player ${playerId}`);
        const nameLabel = createTextSprite(playerName);
        nameLabel.position.set(0, 2.5, 0); // Position above head
        
        // Add name label to player and track in nameLabels map
        newPlayer.add(nameLabel);
        this.nameLabels.set(playerId, nameLabel);
        
        // Log player created
        console.log('Remote player added to scene:', {
            id: playerId,
            name: playerName,
            position: playerData.position,
            sceneChildren: this.scene.children.length
        });
        
        // Update player with initial data
        this.updateRemotePlayer(playerId, playerData);
    }

    private updateRemotePlayer(playerId: string, playerData: any) {
        // Get player from map
        const player = this.remotePlayers.get(playerId);
        
        if (!player) {
            console.warn(`Tried to update player ${playerId} but they don't exist`);
            return;
        }
        
        // Get player position and rotation from data
        const position = playerData.position;
        const rotation = playerData.rotation;
        const isWalking = playerData.game_data?.isWalking || false;
        const isDancing = playerData.game_data?.isDancing || false;
        const danceStopFlag = playerData.game_data?.danceStopFlag || false;
        
        console.log(`Remote player ${playerId} update - isDancing: ${isDancing}, danceStopFlag: ${danceStopFlag}`);
        
        if (danceStopFlag) {
            
            // Update our local tracking of dance stop flags
            this.remoteDanceStopFlags.set(playerId, true);
            
            // Immediately stop dancing with the direct emergency stop
            this.emergencyStopDancing(player);
            
            // Force state update
            player.userData.isDancing = false;
            player.userData.currentDancing = false;
        }
        // Otherwise proceed with normal dance state changes
        else if (isDancing !== player.userData.isDancing) {
            
            // Immediately update animations
            if (isDancing) {
                this.startRemotePlayerDancing(player);
            } else {
                this.stopRemotePlayerDancing(player);
            }
        }
        
        // Store states for animation system
        player.userData.isWalking = isWalking;
        player.userData.isDancing = isDancing;
        
        // Only update position and rotation if player has moved and is not dancing
        if (position && rotation) {
            // If dancing, keep position but update rotation
            if (isDancing) {
                player.rotation.y = rotation.y;
            } else {
                player.position.set(position.x, position.y, position.z);
                player.rotation.y = rotation.y;
            }
        }
        
        // Only update walking animations if not dancing
        if (!isDancing) {
            this.updateRemotePlayerWalkingAnimation(player, isWalking);
        }
        
        // Update name label position
        const nameLabel = this.nameLabels.get(playerId);
        if (nameLabel) {
            nameLabel.position.set(0, 2.5, 0);
        }
    }
    
    private startRemotePlayerDancing(player: THREE.Group) {
        if (!player.userData.mixer || !player.userData.animations) {
            console.log("Can't start dancing - no mixer or animations");
            return;
        }
        
        console.log("Starting remote player dance with direct approach");
        const animations = player.userData.animations;
        const mixer = player.userData.mixer;
        
        // If no dance animation yet, load it
        if (!animations.dancing) {
            this.loadDanceAnimationForRemotePlayer(player);
            return;
        }
        
        // Stop other animations first
        if (animations.walking) {
            animations.walking.stop();
            animations.walking.enabled = false;
            animations.walking.setEffectiveWeight(0);
        }
        
        if (animations.idle) {
            animations.idle.stop();
            animations.idle.enabled = false;
            animations.idle.setEffectiveWeight(0);
        }
        
        // Start dance animation with clean state
        animations.dancing.reset();
        animations.dancing.enabled = true;
        animations.dancing.setEffectiveTimeScale(1);
        animations.dancing.setEffectiveWeight(1);
        animations.dancing.setLoop(THREE.LoopRepeat, Infinity);
        animations.dancing.play();
        
        // Force mixer update to ensure animation starts correctly
        mixer.update(0);
        mixer.update(0.01);
        
        // Update player state
        player.userData.currentDancing = true;
        player.userData.currentWalking = false;
        player.userData.dancingInitialized = true;
    }
    
    private stopRemotePlayerDancing(player: THREE.Group) {
        if (!player.userData.mixer || !player.userData.animations) {
            console.log("Can't stop dancing - no mixer or animations");
            return;
        }
        
        console.log("Stopping remote player dance with direct approach");
        const animations = player.userData.animations;
        const mixer = player.userData.mixer;
        
        if (animations.dancing) {
            // Stop dancing animation completely
            animations.dancing.stop();
            animations.dancing.enabled = false;
            animations.dancing.setEffectiveWeight(0);
            
            try {
                // Get the clip and uncache it
                const clip = animations.dancing.getClip();
                mixer.uncacheClip(clip);
            } catch (e) {
                console.log("Error uncaching dance clip:", e);
            }
            
            // Force mixer update to clear animation
            mixer.update(0);
        }
        
        // Reset animation state
        player.userData.currentDancing = false;
        player.userData.dancingInitialized = false;
        
        // Start idle animation
        if (animations.idle) {
            animations.idle.reset();
            animations.idle.enabled = true;
            animations.idle.setEffectiveTimeScale(1);
            animations.idle.setEffectiveWeight(1);
            animations.idle.play();
            
            // Force mixer update to apply idle immediately
            mixer.update(0.01);
        }
    }
    
    private updateRemotePlayerWalkingAnimation(player: THREE.Group, isWalking: boolean) {
        if (!player.userData.mixer || !player.userData.animations) {
            return;
        }
        
        const animations = player.userData.animations;
        const mixer = player.userData.mixer;
        
        // Skip if currently dancing
        if (player.userData.currentDancing) {
            return;
        }
        
        // Handle walking/idle transitions
        if (isWalking && !player.userData.currentWalking) {
            // Transition from idle to walking
            if (animations.idle) {
                animations.idle.stop();
                animations.idle.enabled = false;
            }
            if (animations.walking) {
                animations.walking.reset();
                animations.walking.enabled = true;
                animations.walking.setEffectiveTimeScale(1);
                animations.walking.setEffectiveWeight(1);
                animations.walking.play();
                
                // Force mixer update to apply walking immediately
                mixer.update(0.01);
            }
            player.userData.currentWalking = true;
        } else if (!isWalking && player.userData.currentWalking) {
            // Transition from walking to idle
            if (animations.walking) {
                animations.walking.stop();
                animations.walking.enabled = false;
            }
            if (animations.idle) {
                animations.idle.reset();
                animations.idle.enabled = true;
                animations.idle.setEffectiveTimeScale(1);
                animations.idle.setEffectiveWeight(1);
                animations.idle.play();
                
                // Force mixer update to apply idle immediately
                mixer.update(0.01);
            }
            player.userData.currentWalking = false;
        }
    }

    private loadDanceAnimationForRemotePlayer(player: THREE.Group) {
        // Only load if we have a mixer
        if (!player.userData.mixer) {
            console.error("Cannot load dance animation - no mixer available");
            return;
        }
        
        console.log("Loading dance animation for remote player, current userData:", player.userData);
        const fbxLoader = new FBXLoader();
        fbxLoader.load('/assets/dancing.fbx', 
            // Success callback
            (danceData: THREE.Group) => {
                console.log("Remote player dance animation loaded successfully");
                
                // Create the dance animation action with proper looping setup
                const danceAnimation = player.userData.mixer.clipAction(danceData.animations[0]);
                
                // Ensure infinite looping is properly configured
                danceAnimation.reset();
                danceAnimation.enabled = true;
                danceAnimation.setLoop(THREE.LoopRepeat, Infinity);
                danceAnimation.setEffectiveTimeScale(1);
                danceAnimation.setEffectiveWeight(1);
                danceAnimation.clampWhenFinished = true;
                danceAnimation.zeroSlopeAtEnd = false; // Prevent weird transitions at loop points
                danceAnimation.zeroSlopeAtStart = false;
                
                // Store the animation
                console.log("Adding dance animation to remote player userData.animations");
                player.userData.animations.dancing = danceAnimation;
                
                // If the player is supposed to be dancing, start the animation
                if (player.userData.isDancing) {
                    console.log("Remote player should be dancing - starting animation immediately");
                    // Stop other animations with proper cleanup
                    if (player.userData.animations.walking) {
                        player.userData.animations.walking.stop();
                        player.userData.animations.walking.enabled = false;
                        player.userData.animations.walking.setEffectiveWeight(0);
                    }
                    if (player.userData.animations.idle) {
                        player.userData.animations.idle.stop();
                        player.userData.animations.idle.enabled = false;
                        player.userData.animations.idle.setEffectiveWeight(0);
                    }
                    
                    // Start dancing with forced looping enabled
                    player.userData.animations.dancing.play();
                    
                    // Update the animation settings one more time after play to ensure they stick
                    player.userData.animations.dancing.paused = false;
                    player.userData.animations.dancing.enabled = true;
                    player.userData.animations.dancing.setLoop(THREE.LoopRepeat, Infinity);
                    
                    // Apply multiple mixer updates to ensure animation starts properly
                    player.userData.mixer.update(0);
                    player.userData.mixer.update(0.01);
                    player.userData.mixer.update(0.02);
                    
                    // Mark as dancing with all the flags we need
                    player.userData.currentDancing = true;
                    player.userData.currentWalking = false;
                    player.userData.dancingInitialized = true;
                    
                    // Force an extra update to ensure the animation is properly started
                    setTimeout(() => {
                        if (player.userData.isDancing) {
                            console.log("Extra dance animation update for remote player");
                            player.userData.mixer.update(0.1);
                            
                            // Double check that dancing is still enabled
                            if (player.userData.animations.dancing) {
                                player.userData.animations.dancing.enabled = true;
                                player.userData.animations.dancing.paused = false;
                                player.userData.animations.dancing.setLoop(THREE.LoopRepeat, Infinity);
                            }
                        }
                    }, 100);
                }
            },
            // Progress callback
            (xhr) => {
                console.log(`Loading dance animation: ${Math.floor(xhr.loaded / xhr.total * 100)}%`);
            },
            // Error callback
            (error) => {
                console.error("Error loading dance animation for remote player:", error);
            }
        );
    }

    private removeRemotePlayer(playerId: string) {
        const remotePlayer = this.remotePlayers.get(playerId);
        if (remotePlayer) {
            // Remove name label
            const nameLabel = this.nameLabels.get(playerId);
            if (nameLabel) {
                remotePlayer.remove(nameLabel);
                this.nameLabels.delete(playerId);
            }

            this.scene.remove(remotePlayer);
            this.remotePlayers.delete(playerId);
            console.log('Remote player removed from scene:', {
                id: playerId,
                sceneChildren: this.scene.children.length
            });
        }
    }

    private createPlayerInstance(): THREE.Group {
        // Instead of cloning, we'll create a new instance of the same model
        const remotePlayerGroup = new THREE.Group();
        remotePlayerGroup.name = 'remotePlayer';
        
        // Create a placeholder cube until the model loads
        const placeholder = new THREE.Mesh(
            new THREE.BoxGeometry(0.5, 1.8, 0.5),
            new THREE.MeshBasicMaterial({ color: 0x4444ff, wireframe: true })
        );
        placeholder.position.y = 0.9; // Half the height
        remotePlayerGroup.add(placeholder);
        
        // We need to load the character model directly
        const fbxLoader = new FBXLoader();
        console.log('Loading remote player model...');
        
        // Load walking animation which includes the model
        fbxLoader.load('/assets/walking.fbx', (walkingData: THREE.Group) => {
            console.log('Remote player walking model loaded successfully');
            
            // Scale the model appropriately
            walkingData.scale.set(0.013, 0.013, 0.013);
            
            // Create animation mixer
            const mixer = new THREE.AnimationMixer(walkingData);
            const walkingAnimation = mixer.clipAction(walkingData.animations[0]);
            walkingAnimation.setEffectiveWeight(0); // Start with weight 0
            walkingAnimation.play(); // Play but with 0 weight
            
            // Load idle animation
            fbxLoader.load('/assets/idle.fbx', (idleData: THREE.Group) => {
                console.log('Remote player idle animation loaded successfully');
                
                const idleAnimation = mixer.clipAction(idleData.animations[0]);
                idleAnimation.setEffectiveWeight(1); // Start with full weight
                idleAnimation.play();
                
                // Remove placeholder
                remotePlayerGroup.remove(placeholder);
                
                // Add the model to the remote player group
                remotePlayerGroup.add(walkingData);
                
                // Make remote players visually distinct
                walkingData.traverse((child) => {
                    if ((child as THREE.Mesh).isMesh) {
                        const mesh = child as THREE.Mesh;
                        if (mesh.material) {
                            // Apply a blue tint to the material
                            const matArray = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                            matArray.forEach((mat: THREE.Material) => {
                                if ((mat as any).color) {
                                    // Apply a slight blue tint to make remote players distinguishable
                                    (mat as any).color.multiplyScalar(0.8).add(new THREE.Color(0, 0, 0.2));
                                }
                            });
                        }
                    }
                });
                
                // Store animations and mixer in userData
                remotePlayerGroup.userData = {
                    mixer,
                    animations: {
                        idle: idleAnimation,
                        walking: walkingAnimation,
                        dancing: null // This will be loaded on demand
                    },
                    modelLoaded: true,
                    isWalking: false,
                    isDancing: false,
                    currentWalking: false,
                    currentDancing: false
                };
                
                console.log('Remote player instance created successfully');
            });
        });
        
        return remotePlayerGroup;
    }

    public connect(): Promise<void> {
        console.log('Connecting to multiplayer server...');
        
        // Initialize chat UI after connection
        const connectPromise = this.client.connect();
        
        // Initialize chat UI
        this.initChatUI();
        
        return connectPromise;
    }
    
    private initChatUI(): void {
        // Create chat UI if it doesn't exist
        if (!this.chatUI) {
            console.log('Initializing chat UI...');
            this.chatUI = new ChatUI(this.client);
        }
    }

    public update(deltaTime: number = 0.016): void {
        // Ensure we have a client
        if (!this.client) {
            return;
        }

        // Only send updates if we've moved or changed state
        let shouldSendUpdate = false;
        
        // Get player position and rotation
        const position = {
            x: this.localPlayer.model.position.x,
            y: this.localPlayer.model.position.y,
            z: this.localPlayer.model.position.z
        };
        
        const rotation = {
            y: this.localPlayer.model.rotation.y
        };
        
        // Check if we have a valid controller
        if (!this.localPlayer.controller) {
            return;
        }
        
        // Detect walking state if the controller has that method
        let isWalking = false;
        if (typeof this.localPlayer.controller.getIsWalking === 'function') {
            isWalking = this.localPlayer.controller.getIsWalking();
        }
        
        // Detect dancing state if the controller has that method
        let isDancing = false;
        if (typeof this.localPlayer.controller.getIsDancing === 'function') {
            isDancing = this.localPlayer.controller.getIsDancing();
        }
        
        // Check if position has changed
        if (!this.lastPosition ||
            Math.abs(position.x - this.lastPosition.x) > 0.01 ||
            Math.abs(position.y - this.lastPosition.y) > 0.01 ||
            Math.abs(position.z - this.lastPosition.z) > 0.01) {
            shouldSendUpdate = true;
            this.lastPosition = position;
        }
        
        // Check if rotation has changed
        if (!this.lastRotation ||
            Math.abs(rotation.y - this.lastRotation.y) > 0.01) {
            shouldSendUpdate = true;
            this.lastRotation = rotation;
        }
        
        // Check if important game state has changed (walking, dancing)
        // Always check for walking state changes to fix stuck animations
        const lastIsWalking = (this.localPlayer.controller as any).userData?.lastIsWalking;
        const lastIsDancing = (this.localPlayer.controller as any).userData?.lastIsDancing;
        
        if (isWalking !== lastIsWalking || isDancing !== lastIsDancing) {
            shouldSendUpdate = true;
            
            // Store last state in userData for comparison next time
            if (!(this.localPlayer.controller as any).userData) {
                (this.localPlayer.controller as any).userData = {};
            }
            (this.localPlayer.controller as any).userData.lastIsWalking = isWalking;
            (this.localPlayer.controller as any).userData.lastIsDancing = isDancing;
            
            // Log important state changes to help with debugging
            if (isWalking !== lastIsWalking) {
                console.log(`Local player walking state changed: ${lastIsWalking} → ${isWalking}`);
            }
            if (isDancing !== lastIsDancing) {
                console.log(`Local player dancing state changed: ${lastIsDancing} → ${isDancing}`);
                
                // Special handling for dance stopping
                if (lastIsDancing && !isDancing) {
                    console.log("⚠️ LOCAL PLAYER STOPPED DANCING - sending immediate danceStopFlag");
                    
                    // Send immediate dance stop update with flag
                    this.client.updatePosition(
                        position,
                        rotation,
                        {
                            name: this.playerName,
                            isWalking: isWalking,
                            isDancing: false,
                            danceStopFlag: true
                        }
                    );
                }
            }
        }
        
        // If we've moved or changed state, send an update
        if (shouldSendUpdate) {
            // Send more frequent updates when walking state changes to ensure animations sync properly
            this.client.updatePosition(
                position,
                rotation,
                {
                    name: this.playerName,
                    isWalking: isWalking,
                    isDancing: isDancing
                }
            );
        }
        
        // Update remote players
        for (const [playerId, remotePlayer] of this.remotePlayers.entries()) {
            // Make sure the mixer exists
            if (remotePlayer.userData.mixer) {
                // Update animation mixer
                remotePlayer.userData.mixer.update(deltaTime);
                
                // HIGHEST PRIORITY: If we have a dance stop flag for this player, force stop dancing
                if (this.remoteDanceStopFlags.get(playerId) && remotePlayer.userData.currentDancing) {
                    console.log(`⚠️ APPLYING DANCE STOP FLAG FOR ${playerId}`);
                    this.emergencyStopDancing(remotePlayer);
                    // Keep the flag for several frames to ensure it takes effect
                    setTimeout(() => {
                        this.remoteDanceStopFlags.delete(playerId);
                    }, 1000);
                }
                // Otherwise normal dance state checking
                else if (remotePlayer.userData.isDancing && !remotePlayer.userData.currentDancing) {
                    console.log(`Remote player ${playerId} should be dancing but isn't - starting dance`);
                    if (!remotePlayer.userData.animations?.dancing) {
                        this.loadDanceAnimationForRemotePlayer(remotePlayer);
                    } else {
                        this.startRemotePlayerDancing(remotePlayer);
                    }
                } else if (!remotePlayer.userData.isDancing && remotePlayer.userData.currentDancing) {
                    console.log(`Remote player ${playerId} should NOT be dancing but is - stopping dance`);
                    this.stopRemotePlayerDancing(remotePlayer);
                }
                
                // Check for walking animation that might be stuck
                if (!remotePlayer.userData.isDancing && !remotePlayer.userData.isWalking && remotePlayer.userData.currentWalking) {
                    console.log(`Remote player ${playerId} may have stuck walking animation - forcing idle`);
                    this.updateRemotePlayerWalkingAnimation(remotePlayer, false);
                }
            }
        }
    }

    public disconnect(): void {
        console.log('Disconnecting from multiplayer server...');
        
        // Clear heartbeat interval
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        
        this.client.disconnect();
        
        // Clean up chat UI
        if (this.chatUI) {
            // Hide the chat UI
            this.chatUI.hide();
            this.chatUI = null;
        }
        
        // Clean up scoring system
        this.gameScoring.cleanup();
        
        // Clean up remote players
        this.remotePlayers.forEach((player, id) => {
            this.removeRemotePlayer(id);
        });
        
        console.log('Disconnected from multiplayer server');
    }

    public createTestPlayer(): void {
        console.log('Creating test remote player...');
        
        // Create a test player with a random ID
        const testPlayerId = 'test-player-' + Math.random().toString(36).substr(2, 9);
        
        // Create test player data
        const testPlayerData = {
            player_id: testPlayerId,
            position: {
                x: this.localPlayer.model.position.x + 2, // Position 2 units to the right of local player
                y: this.localPlayer.model.position.y,
                z: this.localPlayer.model.position.z
            },
            rotation: {
                y: 0
            },
            game_data: {
                name: 'Test Player'
            }
        };
        
        // Create the remote player
        this.createRemotePlayer(testPlayerId, testPlayerData);
        
        console.log('Test remote player created:', {
            id: testPlayerId,
            position: testPlayerData.position,
            remotePlayers: this.remotePlayers.size,
            sceneChildren: this.scene.children.length
        });
    }

    public forceDanceUpdate(isDancing: boolean): void {
        console.log(`Force dance update - isDancing: ${isDancing}`);
        if (!this.client) {
            console.error("Cannot force dance update: client not available");
            return;
        }

        // Get player position and rotation
        const position = {
            x: this.localPlayer.model.position.x,
            y: this.localPlayer.model.position.y,
            z: this.localPlayer.model.position.z
        };
        
        const rotation = {
            y: this.localPlayer.model.rotation.y
        };
        
        // Store last known values for future comparisons
        this.lastPosition = position;
        this.lastRotation = rotation;
        
        // Store dance state for future comparisons
        if (!(this.localPlayer.controller as any).userData) {
            (this.localPlayer.controller as any).userData = {};
        }
        (this.localPlayer.controller as any).userData.lastIsDancing = isDancing;
        
        // WHEN STOPPING DANCE: Include the special dance stop flag
        const gameData = {
            name: this.playerName,
            isWalking: false,
            isDancing: isDancing
        };
        
        // Add dance stop flag only when stopping dancing - this is the critical part
        if (!isDancing) {
            console.log("🚨 ADDING DANCE STOP FLAG TO UPDATE");
            (gameData as any).danceStopFlag = true;
        }
        
        // Send multiple messages to ensure it's received
        const messageCount = isDancing ? 3 : 10; // More messages when stopping dance
        const interval = isDancing ? 300 : 100; // More frequent when stopping dance
        
        for (let i = 0; i < messageCount; i++) {
            setTimeout(() => {
                console.log(`Sending dance ${isDancing ? "START" : "STOP"} update ${i+1}/${messageCount}`);
                this.client.updatePosition(
                    position,
                    rotation,
                    gameData
                );
            }, i * interval);
        }
    }

    // Add methods to control third-party game scoring
    public startThirdPartyGameScoring() {
        this.gameScoring.startThirdPartyGameScoring();
    }

    public stopThirdPartyGameScoring() {
        this.gameScoring.stopThirdPartyGameScoring();
    }

    public addPoints(points: number) {
        this.gameScoring.addPoints(points);
    }
    
    public updateTotalScore(points: number) {
        // This method ensures the total score is updated and preserved
        // No need to call addPoints here since we're just finalizing the current session's points
        // The total score is already being tracked in GameScoring
    }

    public getPlayerScore(): number {
        return this.gameScoring.getScore();
    }

    private emergencyStopDancing(player: THREE.Group): void {
        console.log("🛑 EMERGENCY STOP DANCING");
        if (!player.userData.mixer) {
            return;
        }
        
        // NUCLEAR OPTION: Complete reset of animation system
        const mixer = player.userData.mixer;
        
        // Stop ALL animations immediately
        mixer.stopAllAction();
        
        // Reset animation flags
        player.userData.currentDancing = false;
        player.userData.dancingInitialized = false;
        player.userData.isDancing = false;
        
        // Force clean slate for animations
        if (player.userData.animations) {
            if (player.userData.animations.dancing) {
                player.userData.animations.dancing = null;
            }
            
            // Restart idle animation from scratch
            if (player.userData.animations.idle) {
                player.userData.animations.idle.reset();
                player.userData.animations.idle.enabled = true;
                player.userData.animations.idle.setEffectiveTimeScale(1);
                player.userData.animations.idle.setEffectiveWeight(1);
                player.userData.animations.idle.play();
                mixer.update(0.01);
            }
        }
    }
} 